// Inspecteur → Doorgiften (Art.46 international-transfer register, Chapter V) — FRONTEND-BLUEPRINT §3.1 / §4.
// Re-skin on the shadcn design system: PageHeader + DataTable, reusing api.listTransfers()/createTransfer()
// + api.listTransferMechanisms() (no new endpoints). Each row is a transfer_impact_assessment: the Art.46
// safeguard mechanism + the Schrems-II 6-step assessment + conclusion.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { humanizeEnum } from "../../lib/enum-labels";
import type { Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  RegisterScreen, EditDialog, DeactivateButton, StatusBadge, TagBadge, truncate, errText, type Row, type FormField,
} from "./_shared";

const STATUSES = ["draft", "in_review", "approved", "superseded"];
const TIA_FIELDS: { key: string; label: string }[] = [
  { key: "step1MapTransfer", label: "1 · Doorgifte in kaart brengen" },
  { key: "step2IdentifyMechanism", label: "2 · Art.46-mechanisme identificeren" },
  { key: "step3AssessThirdCountryLaw", label: "3 · Recht van het derde land beoordelen" },
  { key: "governmentAccessAssessment", label: "Overheidstoegang-beoordeling" },
  { key: "step5ProceduralSteps", label: "5 · Procedurele stappen" },
  { key: "step6Reevaluate", label: "6 · Periodiek herbeoordelen" },
  { key: "conclusion", label: "Conclusie" },
];

const EDIT_FIELDS: FormField[] = [
  { key: "status", label: "Status", type: "select", required: true, options: STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") })) },
  ...TIA_FIELDS.map((f) => ({ key: f.key, label: f.label, type: "textarea" as const })),
];

export function InspectorTransfersRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["transfers"] });
  const [editing, setEditing] = useState<string | null>(null);

  const columns: Column<Row>[] = [
    { key: "mechanism", header: "Mechanisme", render: (r) => <span className="font-medium">{String(r.mechanism ?? "—")}</span> },
    { key: "mechanismKind", header: "Soort", render: (r) => <TagBadge value={r.mechanismKind} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "conclusion", header: "Conclusie", render: (r) => truncate(r.conclusion, 60) },
  ];

  return (
    <>
      <RegisterScreen
        title={t("nav.transfers")}
        description={t("inspector.transfers.sub")}
        help={t("help.transfers")}
        queryKey="transfers"
        listFn={() => api.listTransfers()}
        columns={columns}
        empty={t("inspector.transfers.empty")}
        readPerm="transfer_impact_assessment.read"
        actions={
          <Can perm="transfer_impact_assessment.write">
            <CreateTransferDialog onDone={invalidate} />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="transfer_impact_assessment.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="transfer_impact_assessment.delete"><DeactivateButton basePath={TENANT_PATHS.transfers} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.transfers}
          id={editing}
          title={t("inspector.transfers.edit")}
          fields={EDIT_FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}

/** Bespoke create dialog: a transfer needs a seeded Art.46 mechanism (owner/counsel content) + the TIA steps. */
function CreateTransferDialog({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Record<string, string>>({ status: "draft" });
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => setF({ status: "draft" });
  const mechanisms = (useQuery({ queryKey: ["transfer-mechanisms"], queryFn: () => api.listTransferMechanisms(), enabled: open }).data ?? []) as Row[];
  const verwerkingen = (useQuery({ queryKey: ["verwerkingen"], queryFn: () => api.listVerwerkingen(), enabled: open }).data ?? []) as Row[];
  const mut = useMutation({
    mutationFn: () => api.createTransfer({
      transferMechanismId: f.transferMechanismId,
      ...(f.verwerkingId ? { verwerkingId: f.verwerkingId } : {}),
      status: f.status,
      ...Object.fromEntries(TIA_FIELDS.map((x) => [x.key, f[x.key] ?? ""]).filter(([, v]) => v)),
    }),
    onSuccess: () => { reset(); setOpen(false); onDone(); },
  });
  const selectCls = "h-8 w-full rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); mut.reset(); } }}>
      <DialogTrigger asChild>
        <Button size="sm">{t("crud.new")}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("inspector.transfers.new")}</DialogTitle>
          <DialogDescription>{t("inspector.transfers.sub")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="t-mech">{t("inspector.transfers.mechanism")} *</Label>
            <select id="t-mech" value={f.transferMechanismId ?? ""} onChange={(e) => set("transferMechanismId", e.target.value)} className={selectCls}>
              <option value="">{t("crud.select")}</option>
              {mechanisms.map((m) => <option key={String(m.id)} value={String(m.id)}>{String(m.displayName)} ({humanizeEnum(m.mechanismKind)})</option>)}
            </select>
            {mechanisms.length === 0 && <p className="text-xs text-muted-foreground">{t("inspector.transfers.noMechanisms")}</p>}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="t-verw">{t("inspector.transfers.linkedVerwerking")}</Label>
            <select id="t-verw" value={f.verwerkingId ?? ""} onChange={(e) => set("verwerkingId", e.target.value)} className={selectCls}>
              <option value="">—</option>
              {verwerkingen.map((v) => <option key={String(v.id)} value={String(v.id)}>{String(v.name)}</option>)}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="t-status">Status</Label>
            <select id="t-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={selectCls}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          {TIA_FIELDS.map((x) => (
            <div key={x.key} className="grid gap-1.5">
              <Label htmlFor={`t-${x.key}`}>{x.label}</Label>
              <Textarea id={`t-${x.key}`} rows={2} value={f[x.key] ?? ""} onChange={(e) => set(x.key, e.target.value)} />
            </div>
          ))}
          {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); mut.reset(); }}>{t("common.cancel")}</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !f.transferMechanismId}>
            {mut.isPending ? t("crud.creating") : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
